import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { useLanguage } from '@/contexts/language-context';
import { formatMoney } from '@/lib/utils';
import { listPayrollPayments, payrollPaymentsQueryKey } from '@/lib/api/payroll';

import { PaymentDetailModal } from '../payment-detail-modal';

interface Props {
  businessId: string;
  employeeId: string;
}

export function PaymentHistoryPanel({ businessId, employeeId }: Props) {
  const { t } = useLanguage();
  const [openPaymentId, setOpenPaymentId] = useState<string | null>(null);

  const paymentsQuery = useQuery({
    queryKey: payrollPaymentsQueryKey(businessId, employeeId),
    queryFn: () => listPayrollPayments(businessId, employeeId),
  });

  const payments = paymentsQuery.data ?? [];

  return (
    <div className="flex flex-col gap-3">
      <h2 className="text-sm font-semibold">{t.payrollViewHistory}</h2>

      {paymentsQuery.isPending ? (
        <p className="text-sm text-muted-foreground py-4">{t.loading}</p>
      ) : payments.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4">{t.payrollHistoryEmpty}</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {payments.map((payment) => (
            <li key={payment.id}>
              <button
                type="button"
                onClick={() => setOpenPaymentId(payment.id)}
                className="w-full flex items-center justify-between gap-3 px-4 py-2.5 rounded-lg border bg-card text-left hover:shadow-sm transition-shadow"
                data-testid={`payroll-payment-history-row-${payment.id}`}
              >
                <span className="text-sm text-muted-foreground">
                  {new Date(payment.paid_at).toLocaleDateString()}
                </span>
                <span className="text-sm font-semibold">
                  {formatMoney(payment.total_amount)}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {openPaymentId && (
        <PaymentDetailModal
          businessId={businessId}
          paymentId={openPaymentId}
          onClose={() => setOpenPaymentId(null)}
        />
      )}
    </div>
  );
}
